import { useTranslation } from 'react-i18next';
import { HealthBandPill } from '../../components/domain/HealthBandPill';
import { isDemo } from '../../demo/flag';
import { pctLabel, signedCurr } from '../../utils/price';
import { useOpsUser } from '../ownership/opsUser';
import { useLaneHint } from './hooks';
import styles from './LaneHint.module.css';

interface Props {
  originCity: string | null | undefined;
  destinationCity: string | null | undefined;
}

/**
 * One line of lane history for pricing and awarding (#300): "Delhi → Mumbai, last 90 days: 12 orders,
 * average margin ₹4,200 (6.1 %)". Manager only (P9); never blocks the form (P1): nothing while loading,
 * on error, or with no orders. Demo world only until rhino-api ships GET /admin/profit/lane-hint.
 */
export function LaneHint(props: Props) {
  return isDemo() ? <ManagerLaneHint {...props} /> : null;
}

function ManagerLaneHint({ originCity, destinationCity }: Props) {
  const { t } = useTranslation();
  const { isManager } = useOpsUser();
  const origin = originCity?.trim() ?? '';
  const destination = destinationCity?.trim() ?? '';
  const { data } = useLaneHint(origin, destination, isManager && !!origin && !!destination);
  if (!isManager || !data || data.orders <= 0) return null;
  return (
    <p className={styles.hint} data-testid="lane-hint">
      <span>
        {t('profit.laneHint.line', {
          lane: data.label,
          days: data.days,
          count: data.orders,
          margin: signedCurr(data.averageMargin),
          pct: pctLabel(data.marginPct),
        })}
      </span>
      {data.healthBand && <HealthBandPill band={data.healthBand} />}
    </p>
  );
}
